import React, { useCallback, useRef, useState } from "react";
import { StandardPageWrapper } from "../../../../components/layout/StandardPageWrapper";
import { Routes } from "../../../../root/routes";
import { usePrimaryOrganizationId } from "../../../../hooks";
import { Breadcrumbs, Button, Modal } from "@truevault/aquilo";
import { Api } from "../../../../../common/service/Api";
import { useDataRequest } from "../../../../../common/hooks/api";
import { useApiSnackbar } from "../../../../../common/service/resources/mutators";
import { OrganizationFileDto } from "../../../../../common/service/server/controller/OrganizationFileController";
import { ArrowDownTrayIcon, TrashIcon, CloudArrowUpIcon } from "@heroicons/react/24/outline";

// Maximum file size in bytes (15MB)
const MAX_FILE_SIZE = 15000000;

// Allowed file extensions
const ALLOWED_EXTENSIONS = [
  ".pdf",
  ".doc",
  ".docx",
  ".txt",
  ".csv",
  ".xlsx",
  ".xls",
  ".png",
  ".jpg",
  ".jpeg",
  ".gif",
];

const isAllowedFileType = (filename: string): boolean => {
  const lowerFilename = filename.toLowerCase();
  return ALLOWED_EXTENSIONS.some((ext) => lowerFilename.endsWith(ext));
};

const formatDate = (dateStr: string): string => {
  if (!dateStr) return "-";
  // ZonedDateTime.toString() produces format like "2025-12-11T10:30:00-08:00[America/Los_Angeles]"
  // We need to strip the bracketed timezone part before parsing
  const cleanedDate = dateStr.replace(/\[.*\]$/, "");
  const date = new Date(cleanedDate);
  if (isNaN(date.getTime())) return "-";
  return date.toLocaleDateString();
};

export const MyFilesPage = () => {
  const orgId = usePrimaryOrganizationId();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number } | null>(
    null,
  );
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<OrganizationFileDto | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { apiSuccess, apiError } = useApiSnackbar();

  const apiRequest = useCallback(() => Api.organizationFiles.getFiles(orgId), [orgId]);

  const {
    result: files,
    request,
    refresh,
  } = useDataRequest({
    queryKey: ["organizationFiles", orgId],
    api: apiRequest,
    notReady: !orgId,
    dependencies: [orgId],
  });

  /**
   * Processes multiple files for upload
   */
  const processFilesForUpload = useCallback(
    async (files: File[]) => {
      if (uploading || files.length === 0) return;

      setUploading(true);
      const filesToUpload: File[] = [];
      const skippedFiles: string[] = [];

      // Validate and filter files
      for (const file of files) {
        if (file.size > MAX_FILE_SIZE) {
          skippedFiles.push(`${file.name} (exceeds 15MB)`);
        } else if (!isAllowedFileType(file.name)) {
          skippedFiles.push(`${file.name} (unsupported type)`);
        } else {
          filesToUpload.push(file);
        }
      }

      if (filesToUpload.length === 0) {
        setUploading(false);
        if (skippedFiles.length > 0) {
          apiError(`No valid files to upload. Skipped: ${skippedFiles.join(", ")}`);
        }
        return;
      }

      // Upload all files
      let successCount = 0;
      let failCount = 0;
      const successfullyUploadedFiles: string[] = [];
      setUploadProgress({ current: 0, total: filesToUpload.length });

      for (let i = 0; i < filesToUpload.length; i++) {
        const file = filesToUpload[i];
        setUploadProgress({ current: i + 1, total: filesToUpload.length });
        try {
          await Api.organizationFiles.uploadFile(orgId, file);
          successCount++;
          successfullyUploadedFiles.push(file.name);
        } catch (error) {
          console.error(`Failed to upload file ${file.name}:`, error);
          failCount++;
        }
      }

      setUploadProgress(null);
      setUploading(false);

      // Refresh file list
      refresh();

      // Show appropriate success/error messages
      if (successCount > 0) {
        if (successCount === 1) {
          apiSuccess(`${successfullyUploadedFiles[0]} was successfully uploaded`);
        } else {
          apiSuccess(`Successfully uploaded ${successCount} files`);
        }
      }

      if (failCount > 0) {
        apiError(`Failed to upload ${failCount} file${failCount !== 1 ? "s" : ""}`);
      }

      if (skippedFiles.length > 0 && successCount > 0) {
        console.warn("Skipped files:", skippedFiles);
      }
    },
    [uploading, orgId, refresh, apiSuccess, apiError],
  );

  const handleFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const fileList = event.target.files;
      if (fileList && fileList.length > 0) {
        const files = Array.from(fileList);
        await processFilesForUpload(files);
      }
      // Reset the input so the same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [processFilesForUpload],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);

      if (uploading) return;

      const files = Array.from(e.dataTransfer.files);
      await processFilesForUpload(files);
    },
    [uploading, processFilesForUpload],
  );

  const handleClick = () => {
    if (!uploading) {
      fileInputRef.current?.click();
    }
  };

  const deleteFile = async (fileId: string) => {
    setIsDeleting(true);
    try {
      await Api.organizationFiles.deleteFile(orgId, fileId);
      apiSuccess("File deleted successfully");
      setDeleteDialogOpen(false);
      setFileToDelete(null);
      refresh();
    } catch (error) {
      console.error("Error deleting file:", error);
      apiError("Unable to delete file, please try again later");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDownload = async (file: OrganizationFileDto) => {
    const downloadUrl = Api.organizationFiles.getDownloadUrl(orgId, file.id);
    try {
      const response = await fetch(downloadUrl, { method: "GET", credentials: "include" });
      if (!response.ok) {
        throw new Error("Failed to download file");
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", file.name);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  const handleDeleteClick = (file: OrganizationFileDto) => {
    setFileToDelete(file);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (fileToDelete) {
      deleteFile(fileToDelete.id);
    }
  };

  return (
    <StandardPageWrapper requests={[request]} className="tw-boundary">
      <Breadcrumbs title="My Files" source="My Documents" sourceRoute={Routes.documents.root} />

      <input
        ref={fileInputRef}
        type="file"
        multiple
        style={{ display: "none" }}
        onChange={handleFileSelect}
        disabled={uploading}
        accept=".pdf,.doc,.docx,.txt,.csv,.xlsx,.xls,.png,.jpg,.jpeg,.gif"
      />

      {/* File Dropper Area */}
      <div
        className="rounded-lg mt-4"
        style={{
          border: "1.5px dashed #726D83",
          background: "#F9F8FB",
          padding: "24px",
        }}
      >
        <div
          className="text-center flex flex-col items-center justify-center p-8 cursor-pointer rounded-lg"
          style={{
            background: dragOver ? "#EBE9F0" : "transparent",
          }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleClick}
        >
          <CloudArrowUpIcon className="h-12 w-12 text-gray-400" />
          <div className="mt-4">
            <p className="text-lg font-medium text-gray-900">
              {uploading
                ? uploadProgress
                  ? `Uploading ${uploadProgress.current} of ${uploadProgress.total} files...`
                  : "Processing..."
                : "Drop files here or click to upload"}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              Upload any documents you want to store securely
            </p>
            <p className="text-xs text-gray-400 mt-1">
              Supports multiple files • PDF, DOC, DOCX, TXT, CSV, XLS, XLSX, PNG, JPG, JPEG, GIF
              (max 15MB per file)
            </p>
          </div>
        </div>
      </div>

      {/* File List */}
      {files && files.length > 0 && (
        <div className="mt-6">
          <table className="w-full">
            <thead>
              <tr className="text-left text-sm font-medium text-gray-700 border-b border-gray-200">
                <th className="pb-3">File Name</th>
                <th className="pb-3">Date Uploaded</th>
                <th className="pb-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {files.map((file) => (
                <tr key={file.id} className="hover:bg-gray-50">
                  <td className="py-3">
                    <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                  </td>
                  <td className="py-3 text-sm text-gray-600">{formatDate(file.createdAt)}</td>
                  <td className="py-3">
                    <div className="flex items-center justify-end space-x-2">
                      <Button
                        color="tertiary"
                        size="m"
                        width="auto"
                        variant="round"
                        isCircular
                        onClick={() => handleDownload(file)}
                      >
                        <ArrowDownTrayIcon className="h-4 w-4" />
                      </Button>
                      <Button
                        color="tertiary"
                        size="m"
                        width="auto"
                        variant="round"
                        isCircular
                        onClick={() => handleDeleteClick(file)}
                        disabled={isDeleting}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteDialogOpen && fileToDelete && (
        <Modal
          onClose={() => {
            setDeleteDialogOpen(false);
            setFileToDelete(null);
          }}
          title={`Are you sure you want to delete ${fileToDelete.name}?`}
          alignText="left"
          closeButton={true}
          showOverlay={true}
          closeOnOverlayClick={true}
        >
          <p className="text-sm text-gray-600">You won't be able to undo this action.</p>
          <div className="mt-6 flex justify-end gap-3">
            <Button
              color="tertiary"
              onClick={() => {
                setDeleteDialogOpen(false);
                setFileToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button color="primary" onClick={confirmDelete} disabled={isDeleting}>
              Delete
            </Button>
          </div>
        </Modal>
      )}
    </StandardPageWrapper>
  );
};
